import { useState, useCallback } from "react";
import { Text, TextInput, View } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
  ContinueButton,
  GLASS_CONTROL_INPUT_STYLE,
  GLASS_CONTROL_SHELL_STYLE,
} from "../components/OnboardingLayout";
import { useTranslations } from "@src/packages/i18n";
import type { OnboardingStackParamList } from "../types";

type Props = NativeStackScreenProps<OnboardingStackParamList, "Birthday">;

/** Format raw digits into MM/DD/YYYY as the user types */
function formatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Parse MM/DD/YYYY string into a Date, returns null if invalid */
function parseDateInput(formatted: string): Date | null {
  const digits = formatted.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const month = parseInt(digits.slice(0, 2), 10);
  const day = parseInt(digits.slice(2, 4), 10);
  const year = parseInt(digits.slice(4, 8), 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Calculate age from date of birth */
function calculateAge(dob: Date): number {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export default function BirthdayScreen({ navigation }: Props) {
  const t = useTranslations();
  const { data, updateField } = useOnboarding();

  const existingFormatted = data.dateOfBirth
    ? (() => {
        const [y, m, d] = data.dateOfBirth!.split("-");
        return `${m}/${d}/${y}`;
      })()
    : "";
  const [dobText, setDobText] = useState(existingFormatted);

  const parsed = parseDateInput(dobText);
  const age = parsed ? calculateAge(parsed) : null;
  const validAge = age !== null && age >= 18 && age <= 65;

  const handleDateChange = useCallback((text: string) => {
    setDobText(formatDateInput(text));
  }, []);

  const handleContinue = () => {
    if (!parsed || !validAge || age === null) return;
    const iso = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
    updateField("dateOfBirth", iso);
    updateField("age", age);
    navigation.navigate("Location");
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.birthday.title")}</ScreenTitle>
      <ScreenSubtitle>{t("onboarding.birthday.subtitle")}</ScreenSubtitle>
      <View style={GLASS_CONTROL_SHELL_STYLE}>
        <TextInput
          value={dobText}
          onChangeText={handleDateChange}
          placeholder={t("onboarding.birthday.placeholder")}
          placeholderTextColor="rgba(255, 255, 255, 0.48)"
          selectionColor="rgba(255, 218, 189, 0.82)"
          keyboardType="number-pad"
          maxLength={10}
          autoFocus
          autoComplete="off"
          textContentType="none"
          inputMode="numeric"
          style={[GLASS_CONTROL_INPUT_STYLE, { flex: 1, letterSpacing: 1.5 }]}
        />
      </View>
      {validAge && age !== null && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: "rgba(255, 255, 255, 0.78)",
            textAlign: "center",
            marginTop: 10,
          }}
        >
          {t("onboarding.birthday.years_old", { params: { age: String(age) } })}
        </Text>
      )}
      <ContinueButton
        title={t("onboarding.next")}
        onPress={handleContinue}
        disabled={!validAge}
      />
    </OnboardingLayout>
  );
}
